import { KnownCreateAccessControlDtoType, KnownSettingsNodeDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback, useMemo } from 'react';
import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import { useQueryGetHub } from '../../../lockers/hubs.queries';
import { useMutationAddAccessCodes, useMutationCreateAccessControl } from '../../../access-control/queries';
import { EntityForm } from '../form.types';
import { AccessControlForm } from './form';
import { AccessControlFormOutput } from './form-output';
import { AccessControlFormSchema } from './form-schema';
import { accessControlFormUISchema } from './ui-schema';
import { AccessControlCodesField } from './access-control-codes-field';

export function useCreateAccessControlForm(
  parentEntityId: string | undefined,
  parentNodeType: KnownSettingsNodeDtoType | undefined,
  parentNodeId: string | undefined
): EntityForm | null {
  const hubResult = useQueryGetHub(parentEntityId);
  const projectResult = useQueryGetProject(parentEntityId);
  const parent = parentNodeType === KnownSettingsNodeDtoType.Hub ? hubResult : projectResult;
  const createMutation = useMutationCreateAccessControl();
  const addCodesMutation = useMutationAddAccessCodes();

  const parentDisplayName = parent.data?.name;

  const defaultValues = useMemo((): AccessControlForm => {
    return {
      attachedToId: parentEntityId,
      type: KnownCreateAccessControlDtoType.Code,
      codes: []
    };
  }, [parentEntityId]);

  const initialFormOutput = useMemo(() => {
    return new AccessControlFormOutput({});
  }, []);

  const formSchema = useMemo(() => {
    return AccessControlFormSchema.FromFormData(parentEntityId, parentDisplayName);
  }, [parentEntityId, parentDisplayName]);

  const onSubmit = useCallback(
    async (formOutput: AccessControlFormOutput) => {
      const result = await createMutation.mutateAsync({
        dto: AccessControlFormOutput.ToCreateDto(formOutput)
      });

      const codes = (formOutput.codes ?? []).filter(Boolean);
      if (codes.length > 0) {
        await addCodesMutation.mutateAsync({ accessControlID: result.id, codes });
      }

      return { entityId: result.id, parentNodeId };
    },
    [createMutation, addCodesMutation, parentNodeId]
  );

  if (!parentEntityId) return null;

  return {
    isLoading: parent.isLoading,
    title: 'New Access Control',
    defaultValues,
    initialFormOutput,
    formSchema,
    uiSchema: accessControlFormUISchema,
    fields: { codesEditor: AccessControlCodesField },
    formContext: { initialCodes: [] },
    getOutput(formData) {
      return new AccessControlFormOutput(formData as AccessControlForm);
    },
    onSubmit,
    isLocked: false,
    lockedMessage: null
  };
}
